import React, { useCallback, useMemo, useRef, useState } from "react";
import { ExpandMore } from "@mui/icons-material";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";

import { ScopeChip } from "fond/topBar/GlobalSearch.styles";
import { getScopeIcon, getScopeLabel } from "fond/topBar/searchScopeUtils";
import { SearchScope } from "fond/topBar/useSearchScope";

const WORKSPACE_SCOPE: SearchScope = { type: "workspace", folderId: null, folderName: null };
const RECENT_SCOPE: SearchScope = { type: "recent", folderId: null, folderName: null };
const STARRED_SCOPE: SearchScope = { type: "starred", folderId: null, folderName: null };
const SHARED_SCOPE: SearchScope = { type: "shared", folderId: null, folderName: null };

interface SearchResultScopeSelectorProps {
  scope: SearchScope;
  scopeFolderName: string | null;
  onScopeChange: (scope: SearchScope) => void;
}

const SearchResultScopeSelector: React.FC<SearchResultScopeSelectorProps> = ({ scope, scopeFolderName, onScopeChange }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const folderScopeRef = useRef<SearchScope | null>(null);

  const resolvedScope: SearchScope = useMemo(
    () => ({
      ...scope,
      folderName: scope.folderName ?? scopeFolderName,
    }),
    [scope, scopeFolderName]
  );

  // Remember the folder/market_lens scope so it stays in the dropdown even after switching away
  if (scope.type === "folder" || scope.type === "market_lens") {
    folderScopeRef.current = resolvedScope;
  }

  const allScopes = useMemo(() => {
    const scopes: SearchScope[] = [WORKSPACE_SCOPE];
    if (folderScopeRef.current) {
      scopes.push(folderScopeRef.current);
    }
    scopes.push(RECENT_SCOPE, STARRED_SCOPE, SHARED_SCOPE);
    return scopes;
  }, [scope.type, resolvedScope]);

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (selected: SearchScope) => {
      onScopeChange(selected);
      setAnchorEl(null);
    },
    [onScopeChange]
  );

  return (
    <>
      <ScopeChip
        data-testid="search-results-scope"
        icon={getScopeIcon(resolvedScope)}
        label={getScopeLabel(resolvedScope)}
        size="small"
        onClick={handleOpen}
        deleteIcon={<ExpandMore sx={{ fontSize: 16 }} />}
        onDelete={handleOpen}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        disableScrollLock
      >
        {allScopes.map((s) => (
          <MenuItem key={`${s.type}-${s.folderId}`} selected={s.type === scope.type && s.folderId === scope.folderId} onClick={() => handleSelect(s)}>
            <ListItemIcon>{getScopeIcon(s)}</ListItemIcon>
            <ListItemText>{getScopeLabel(s)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default SearchResultScopeSelector;
